// /admin/referrals — venue accounts attributed to a referring artist.
//
// Surfaces the North Star §7 attribution data. Money owed and paid lives on
// /admin/referral-payouts; this page is who referred whom.

import { Anchor, Badge, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { EmptyState } from '../../components/ui';
import { adminGet } from './_api';

type ReferralRow = {
  account_id: string;
  account_name: string;
  owner_user_id: string;
  owner_email: string | null;
  owner_display_name: string | null;
  is_founding_host: boolean;
  created_at: number;
  subscription_state: string | null;
  referring_artist: {
    user_id: string;
    display_name: string;
    slug: string | null;
  } | null;
};

type ReferralsList = { items: ReferralRow[] };

export const Route = createFileRoute('/admin/referrals')({
  component: AdminReferrals,
});

function AdminReferrals() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'referrals'],
    queryFn: () => adminGet<ReferralsList>('/api/admin/referrals'),
  });

  return (
    <AdminShell>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Referrals</Title>
          <Text c="dimmed" size="sm">
            Venue accounts attributed to a referring artist via the
            <Text component="code" mx={4}>?ref=</Text>
            capture at sign-up. The artist earns 10% of each venue payment
            (excluding GST) — see Referral payouts for what's owed and paid.
          </Text>
        </Stack>

        {isLoading && <Loader size="sm" />}

        {data && data.items.length === 0 && (
          <EmptyState
            title="No referrals yet."
            body="Once an artist starts inviting venues with their referral link, attributed accounts appear here."
          />
        )}

        {data && data.items.length > 0 && (
          <Table striped highlightOnHover withTableBorder verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Venue account</Table.Th>
                <Table.Th>Host</Table.Th>
                <Table.Th>Referring artist</Table.Th>
                <Table.Th>Subscription</Table.Th>
                <Table.Th>Signed up</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.items.map((row) => {
                const refArtist = row.referring_artist;
                return (
                  <Table.Tr key={row.account_id}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text fw={600}>{row.account_name}</Text>
                        {row.is_founding_host ? (
                          <Badge size="xs" variant="light" color="sun">
                            Founding host
                          </Badge>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{row.owner_display_name ?? '–'}</Text>
                        <Text size="xs" c="dimmed">
                          {row.owner_email ?? '–'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {refArtist === null ? (
                        <Text size="sm" c="dimmed">–</Text>
                      ) : refArtist.slug !== null ? (
                        <Link
                          to="/artists/$slug"
                          params={{ slug: refArtist.slug }}
                          style={{ textDecoration: 'none' }}
                        >
                          <Anchor component="span" size="sm">
                            {refArtist.display_name}
                          </Anchor>
                        </Link>
                      ) : (
                        <Text size="sm">{refArtist.display_name}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.subscription_state ?? '–'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatDate(row.created_at)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </AdminShell>
  );
}

function formatDate(ms: number): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(ms));
}
